'use client';

import type { ReactElement, SetStateAction } from 'react';
import { useMemo, useState } from 'react';
import * as React from 'react';
import { AnimatePresence } from 'framer-motion';
import { createContext } from 'use-context-selector';
import type {
  DndContextProps,
  MeasuringConfiguration,
  SensorOptions,
} from '@dnd-kit/core';
import {
  DndContext as DndKitContext,
  MeasuringFrequency,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

type DndContextValue = {
  /**
   * set the ReactElement to render in the DndKit overlay portal.
   * call this on drag start
   * @see https://docs.dndkit.com/api-documentation/draggable/drag-overlay
   * */
  setOverlay: React.Dispatch<SetStateAction<ReactElement<any> | null>>;
  /**
   * override default DndKit context props for your particular drag case.
   * call this on drag start
   * @see https://docs.dndkit.com/api-documentation/context-provider#props
   */
  setContextProps: React.Dispatch<SetStateAction<DndContextProps | null>>;
};

export const GlobalDndContext = createContext<DndContextValue>({
  setOverlay: () => undefined,
  setContextProps: () => undefined,
});

/** extends dnd-kit's context provider for global, app-wide dnd */
export const DndContextProvider: React.FC<React.PropsWithChildren<unknown>> =
  React.memo(({ children }) => {
    const [contextProps, setContextProps] = useState<DndContextProps | null>(
      null
    );
    const [overlay, setOverlay] = useState<ReactElement<any> | null>(null);

    const defaultSensors = useSensors(
      useSensor(PointerSensor, DEFAULT_POINTER_OPTIONS)
    );

    const value = useMemo(() => ({ setOverlay, setContextProps }), []);

    return (
      <GlobalDndContext.Provider value={value}>
        <DndKitContext
          autoScroll={true}
          collisionDetection={DEFAULT_COLLISION_DETECTION}
          measuring={defaultDndMeasuring}
          sensors={defaultSensors}
          {...contextProps}
        >
          {children}
          <AnimatePresence>{overlay}</AnimatePresence>
        </DndKitContext>
      </GlobalDndContext.Provider>
    );
  });

export const defaultDndMeasuring: MeasuringConfiguration = {
  droppable: {
    strategy: MeasuringStrategy.Always,
    frequency: MeasuringFrequency.Optimized,
  },
};

const DEFAULT_POINTER_OPTIONS: SensorOptions = {
  activationConstraint: {
    distance: 5, // pixels to move before activating
  },
};

const DEFAULT_COLLISION_DETECTION = pointerWithin;
